import React, { useCallback, useState } from "react";
import { Row, Col, Select } from "antd";
import HeroDivider from "/app/src/components/HeroUi/Divider";
import { useTranslation } from "react-i18next";
import AppMapper from "./appMapper";
import { WorkflowAction, App, Report } from "/app/src/models";

/**
 * Renders the Action component for managing workflow actions.
 * @param {object} props - The properties for the Action component.
 * @param {WorkflowAction} [props.action] - The workflow action object.
 * @param {App[]} props.apps - The list of available applications.
 * @param {boolean} props.isNew - Flag indicating if the action is new.
 * @param {(action: WorkflowAction) => Promise<any>} props.addWorkflowAction - Function to add a workflow action.
 * @param {(action: WorkflowAction) => Promise<any>} props.updateAction - Function to update a workflow action.
 * @param {(action: WorkflowAction) => Promise<any> | undefined} props.removeWorkflowAction - Function to remove a workflow action.
 * @param {Report[]} props.reports - List of reports.
 * @returns {JSX.Element} The rendered Action component.
 */
export default function Action({
  action = {},
  apps,
  isNew,
  addWorkflowAction,
  updateAction,
  removeWorkflowAction,
  reports,
}: {
  action?: WorkflowAction;
  apps: App[];
  isNew: boolean;
  addWorkflowAction: (action: WorkflowAction) => Promise<any>;
  updateAction: (action: WorkflowAction) => Promise<any>;
  removeWorkflowAction: (action: WorkflowAction) => Promise<any> | undefined;
  reports: Report[];
}) {
  const { t } = useTranslation();
  const [selectedApp, setSelectedApp] = useState(null);

  const handleOnChange = useCallback((value) => {
    setSelectedApp(value);
  }, []);

  const resetSelectedApp = useCallback(() => {
    setSelectedApp(null);
  }, []);

  const modifiedApps = apps?.map((app) => {
    if (app.name === "Philips Hue") {
      return { ...app, name: "Lights" };
    }
    return app;
  });

  return (
    <div className="workflowAction">
      {isNew && <h3>{t("translation:add_action")}</h3>}
      <Row justify="start" gutter={16}>
        <Col span={4}>
          {isNew ? (
            <Select
              style={{ width: "100%" }}
              size="large"
              placeholder={t("translation:select_app")}
              onChange={handleOnChange}
              value={selectedApp}
            >
              {modifiedApps.map((c) => (
                <Select.Option key={c.id} value={JSON.stringify(c)}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <h3>
              {action.appName === "Philips Hue" ? "Lights" : action.appName}
            </h3>
          )}
        </Col>
        <Col span={20}>
          <AppMapper
            app={
              isNew && selectedApp
                ? JSON.parse(selectedApp)
                : { name: action.appName, id: action.appId }
            }
            action={action}
            isNew={isNew}
            addWorkflowAction={addWorkflowAction}
            updateAction={updateAction}
            removeWorkflowAction={removeWorkflowAction}
            reports={reports}
            resetSelectedApp={resetSelectedApp}
          />
        </Col>
      </Row>
      <HeroDivider>{t("translation:then")}</HeroDivider>
    </div>
  );
}
